import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GWPButton } from 'shared-components';
import {
	createEntertainerSubmissionKey,
	submitEntertainerApplication,
} from '../../Services/EntertainerApplicationService';
import EntertainerApplicationResult from './EntertainerApplicationResult';
import {
	EntertainerApplicationContract,
	EntertainerApplicationFieldErrors,
	EntertainerApplicationFormState,
} from './EntertainerApplicationTypes';
import {
	createInitialForm,
	errorEntries,
	fieldLabels,
	focusFirstError,
	isFormFieldName,
	normalizeServerErrors,
} from './EntertainerApplicationFormUtils';
import { validateEntertainerApplication } from './EntertainerApplicationValidation';
import ActSection from './sections/ActSection';
import AvailabilitySection from './sections/AvailabilitySection';
import ContactSection from './sections/ContactSection';
import PromotionSection from './sections/PromotionSection';
import RequirementsSection from './sections/RequirementsSection';

type Props = { contract: EntertainerApplicationContract };
type Outcome = 'success' | 'duplicate_email' | 'bot_detected';

export default function EntertainerApplicationForm({ contract }: Props) {
	const methods = useForm<EntertainerApplicationFormState>({
		defaultValues: createInitialForm(contract),
		shouldFocusError: false,
	});
	const { clearErrors, formState, handleSubmit, register, setError, setValue } = methods;
	const [submissionKey, setSubmissionKey] = useState(createEntertainerSubmissionKey);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [outcome, setOutcome] = useState<Outcome | null>(null);

	const applyErrors = (rawErrors: EntertainerApplicationFieldErrors) => {
		const errors = normalizeServerErrors(rawErrors);
		for (const [field, message] of Object.entries(errors)) {
			if (isFormFieldName(field)) {
				setError(field, { type: 'manual', message });
			} else {
				setSubmitError(message);
			}
		}
		focusFirstError(errors);
	};

	const submit = handleSubmit(async (form) => {
		setSubmitError(null);
		clearErrors();
		const clientErrors = validateEntertainerApplication(form, contract);
		if (Object.keys(clientErrors).length > 0) {
			applyErrors(clientErrors);
			return;
		}

		try {
			const result = await submitEntertainerApplication(form, submissionKey);
			if (
				result.status === 'success' ||
				result.status === 'duplicate_email' ||
				result.status === 'bot_detected'
			) {
				setOutcome(result.status);
				window.scrollTo({ top: 0, behavior: 'smooth' });
				return;
			}
			applyErrors(result.errors);
			if (result.status === 'image_upload_failed') {
				setSubmitError('The image of your act could not be uploaded. Check the file and try again.');
			}
		} catch (error) {
			setSubmitError(
				error instanceof Error ? error.message : 'Unable to submit your application. Please try again.',
			);
		}
	});

	const startAgain = () => {
		setOutcome(null);
		clearErrors();
		setSubmitError(null);
		setSubmissionKey(createEntertainerSubmissionKey());
		setValue('botDetector', '');
	};

	if (outcome) return <EntertainerApplicationResult status={outcome} onStartAgain={startAgain} />;

	const errors = errorEntries(formState.errors);
	return (
		<>
			<div className="entertainer-intro">
				<p className="eyebrow">Performer applications</p>
				<h1>Bring your act to Goodwill Projects</h1>
				<p>
					Tell us about your act, where you can perform and what you need on the day. We review every
					application and will contact you when a suitable opportunity comes up.
				</p>
				<div className="entertainer-intro-notes">
					<span>Paid performances start from $250</span>
					<span>Busking opportunities are also available</span>
				</div>
			</div>

			<FormProvider {...methods}>
				<form className="entertainer-form" onSubmit={submit} noValidate>
					{errors.length > 0 ? (
						<div className="entertainer-error-summary" role="alert" tabIndex={-1}>
							<h2>Check the highlighted details</h2>
							<ul>
								{errors.map(([field, message]) => (
									<li key={field}>
										<a href={`#${field}`}>
											{fieldLabels[field] ?? 'Application'}: {message}
										</a>
									</li>
								))}
							</ul>
						</div>
					) : null}

					<ActSection contract={contract} />
					<ContactSection />
					<PromotionSection contract={contract} />
					<RequirementsSection contract={contract} />
					<AvailabilitySection contract={contract} />

					<input
						className="entertainer-honeypot"
						type="text"
						tabIndex={-1}
						autoComplete="off"
						aria-hidden="true"
						{...register('botDetector')}
					/>
					<div className="entertainer-submit-panel">
						<div>
							<h2>Ready to apply?</h2>
							<p>Review your details, then send your application to our entertainment team.</p>
						</div>
						<GWPButton
							type="submit"
							size="lg"
							loading={formState.isSubmitting}
							aria-label={formState.isSubmitting ? 'Submitting application' : undefined}
						>
							{formState.isSubmitting ? null : 'Submit application'}
						</GWPButton>
					</div>
					{submitError ? (
						<div className="entertainer-submit-error" role="alert">
							{submitError}
						</div>
					) : null}
				</form>
			</FormProvider>
		</>
	);
}
